import type { Condition, ItemType, SubCondition, UserRole } from '@brickloop/core/schemas';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export const UNAUTHORIZED_EVENT = 'brickloop:unauthorized';

type Query = Record<string, string | number | boolean | undefined | null>;

export function qs(query: Query = {}): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function api<T = unknown>(
  path: string,
  opts: { method?: string; body?: unknown; query?: Query } = {},
): Promise<T> {
  const res = await fetch(`${path}${qs(opts.query)}`, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers: opts.body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'same-origin',
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    if (res.status === 401 && !path.endsWith('/auth/login')) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? res.statusText, err?.details);
  }
  return json as T;
}

// ---------------------------------------------------------------- shapes returned by the API

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
}

export interface LotLocation {
  code: string | null;
  qty: number;
}

export interface LotRow {
  id: number;
  itemId: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  imageUrl: string | null;
  categoryId: number | null;
  categoryName: string | null;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: Condition;
  subCondition: SubCondition | null;
  price: string | null;
  priceLocked: boolean;
  priceRule: string | null;
  bricklinkLotId: number | null;
  /** More that could be built from loose parts (0 unless the item has a parts list). */
  buildable: number;
  remarks: string | null;
  description: string | null;
  bulk: number;
  ean: string | null;
  status: 'draft' | 'live' | 'stockroom' | 'sold_out';
  qtyTotal: number;
  onOrder: number;
  locations: LotLocation[];
  updatedAt: string;
}

export interface Paged<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface Color {
  id: number;
  name: string;
  rgb: string | null;
  /** Solid, Transparent, Chrome, Pearl and so on. */
  colorType?: string | null;
}

export interface Category {
  id: number;
  name: string;
}

export interface CatalogItem {
  id: number;
  itemType: ItemType;
  itemNo: string;
  name: string;
  categoryName: string | null;
  imageUrl: string | null;
}

export interface LocationRow {
  id: number;
  code: string;
  name: string | null;
  /** The bin this one sits inside, if any. */
  parentId: number | null;
  warehouse: string;
  walkOrder: number;
  notes: string | null;
  lots: number;
  pieces: number;
  lastFullCountAt: string | null;
  openSessionId: number | null;
}

export interface StocktakeRow {
  id: number;
  lotId: number | null;
  identityKey: string;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: Condition;
  subCondition: SubCondition | null;
  expectedQty: number;
  countedQty: number | null;
  note: string | null;
  countedAt: string | null;
  difference: number | null;
  kind: 'unchanged' | 'addition' | 'shortage' | 'uncounted';
}

export interface StocktakeSession {
  id: number;
  locationId: number;
  capturedLabel: string;
  state: 'counting' | 'applied' | 'cancelled';
  scope: 'full' | 'partial' | null;
  capturedRevision: number;
  currentRevision: number;
  stale: boolean;
  draftRevision: number;
  note: string | null;
  creatorId: number | null;
  appliedAt: string | null;
  appliedById: number | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  result: StocktakeApplyResult | null;
  rows: StocktakeRow[];
  summary: {
    total: number;
    counted: number;
    uncounted: number;
    additions: number;
    shortages: number;
    unchanged: number;
  };
}

export type StocktakeSessionSummary = Omit<StocktakeSession, 'rows'>;

export interface StocktakeApplyResult {
  sessionId: number;
  locationId: number;
  scope: 'full' | 'partial';
  piecesAdded: number;
  piecesRemoved: number;
  rowsApplied: number;
  allocations: number;
  appliedAt: string;
  appliedBy: number | null;
}

export interface Settings {
  inventoryMode: 'unconsolidated' | 'consolidated';
  defaultCurrency: string;
  /** Whose sales the market sell-through figures count. */
  sellThroughRegion: 'worldwide' | 'uk' | 'europe';
  postageTreatment: 'absorb' | 'spread';
  autoPrice: boolean;
  autoPricePlan: 'market' | 'quick' | 'patient' | 'floor' | 'cost_plus' | 'breakeven';
  neverBelowCost: boolean;
}

export interface PurchaseSummary {
  id: number;
  purchasedOn: string;
  description: string;
  defaultCondition: Condition;
  totalCost: string;
  currency: string;
  sellerName: string | null;
  supplierId: number | null;
  isSystem: boolean;
  /** Hand-made purchases are received. Orders go ordered, in transit, then received as goods are receipted. */
  status: OrderStatus;
  source: 'manual' | 'bricklink' | 'partout';
  externalId: string | null;
  /** Pieces on this order that have not arrived yet. */
  piecesOnOrder: number;
  batches: number;
  piecesBought: number;
  piecesRemaining: number;
}

export interface Dashboard {
  lots: number;
  pieces: number;
  stockValue: string;
  unpricedLots: number;
  lotsWithoutLocation: number;
  purchases: number;
  catalogueItems: number;
  /** Ordered and not yet receipted. Not part of the stock figures. */
  onOrder: { pieces: number; orders: number; value: string; awaitingCount: { pieces: number; shipments: number } };
  channels: { kind: string; mode: string; listings: number; unmatched: number; lastPullAt: string | null }[];
  stock: { costValue: string; listValue: string; potentialProfit: string; pricedPct: number; oldCost: string };
  pricing: { losing: number; thin: number; unpriced: number };
  spend: { last30: string; previous30: string; thisYear: string; byMonth: { month: string; spend: string }[] };
  partOuts: { open: number; piecesLeft: number };
  inboundShipments: { active: number; dueThisWeek: number; piecesDueThisWeek: number; overdue: number };
  topCategories: { name: string; pieces: number; listValue: string; costValue: string }[];
  topLots: { lotId: number; itemNo: string; itemName: string; color: string; condition: string; qty: number; listValue: string }[];
  topSuppliers: { id: number | null; name: string; orders: number; spend: string }[];
  market: { withFigures: number; waiting: number; stale: number; lotsInStock: number };
  sales: { orders: number; pieces: number; revenue: string; cost: string; profit: string; unknownCostPieces: number; everImported: boolean };
}

export interface ChannelStatus {
  kind: 'bricklink' | 'brickowl';
  name: string;
  mode: 'off' | 'read_only' | 'read_write';
  configured: boolean;
  supported: boolean;
  settings: Record<string, unknown>;
  lastPullAt: string | null;
  lastPullStatus: string | null;
  requestsToday: { used: number; cap: number };
  listings: { total: number; matched: number; drift: number; unmatched: number };
}

export interface ResolvedImportRow {
  line: number;
  itemType: ItemType;
  itemNo: string;
  itemName?: string;
  colorId: number | null;
  colorName?: string;
  condition: Condition;
  qty: number;
  price: number | null;
  locationCode?: string | null;
  resolution: {
    status: 'ready' | 'new_item' | 'unknown_item' | 'unknown_color';
    colorName: string | null;
    existing: { lotId: number; qty: number; price: string | null } | null;
  };
}

export interface ImportPreview {
  format: string;
  rows: ResolvedImportRow[];
  skipped: { line: number; reason: string }[];
  warnings: string[];
  locations: { code: string; exists: boolean }[];
  summary: { total: number; ready: number; newItems: number; unresolved: number; pieces: number };
}

export interface RecognitionCandidateRow {
  providerId: string;
  name: string;
  score: number;
  category: string | null;
  itemType: ItemType;
  itemNo: string;
  /** bricklink-link is trustworthy; provider-id is the service's own id, which may not be a BrickLink number. */
  idSource: 'bricklink-link' | 'provider-id';
  item: { id: number; name: string; categoryName: string | null } | null;
  stock: { pieces: number; lots: number };
}

export interface RecognitionResponse {
  provider: string;
  box: { left: number; upper: number; right: number; lower: number; imageWidth: number; imageHeight: number; score: number } | null;
  providerColours: { id: string; name: string; score: number }[];
  candidates: RecognitionCandidateRow[];
}

/** The colours a part comes in, as BrickLink lists them right now. */
export interface ItemColoursResponse {
  /** False when BrickLink could not be asked, so every colour should be offered instead. */
  available: boolean;
  reason?: 'not_connected' | 'budget' | 'unreachable';
  message?: string;
  /** False for sets and minifigures, which have no colour list. */
  applicable: boolean;
  /** True when BrickLink does not know the part number. */
  notOnBrickLink: boolean;
  colors: { id: number; name: string; rgb: string | null; quantity: number | null }[];
  unknownColours: number;
}

// ---------------------------------------------------------------- items and their colour children

export type OrderStatus = 'ordered' | 'in_transit' | 'partially_received' | 'received' | 'closed' | 'cancelled';

export interface ItemListRow {
  id: number;
  itemType: ItemType;
  itemNo: string;
  name: string;
  categoryName: string | null;
  imageUrl: string | null;
  variants: number;
  onHand: number;
  onOrder: number;
  /** A basic record: a name and a category, with BrickLink never asked for the rest. */
  basic: boolean;
  /** The colour to show its picture in. Parts have no picture without one. */
  imageColorId: number | null;
}

export interface VariantRow {
  id: number;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  active: boolean;
  defaultLocationNewId: number | null;
  defaultLocationNewCode: string | null;
  defaultLocationUsedId: number | null;
  defaultLocationUsedCode: string | null;
  notes: string | null;
  onHandNew: number;
  onHandUsed: number;
  onHand: number;
  lots: number;
  onOrder: number;
  onOrderNew: number;
  onOrderUsed: number;
}

export interface IncomingLine {
  purchaseId: number;
  purchaseDescription: string;
  sellerName: string | null;
  externalId: string | null;
  status: OrderStatus;
  purchasedOn: string;
  colorId: number;
  colorName: string;
  condition: Condition;
  qtyOrdered: number;
  qtyReceived: number;
  outstanding: number;
}

export interface ItemDetailData {
  item: {
    id: number;
    itemType: ItemType;
    itemNo: string;
    name: string;
    categoryId: number | null;
    categoryName: string | null;
    imageUrl: string | null;
    weightG: string | null;
    yearReleased: number | null;
    dimX: string | null;
    dimY: string | null;
    dimZ: string | null;
    alternateNo: string | null;
    description: string | null;
    isObsolete: boolean | null;
    bricklinkKnown: boolean | null;
    /** Brick Owl's id for the item, once BrickLoop has matched it. */
    boBoid: string | null;
    detailsFetchedAt: string | null;
    ean: string | null;
    createdAt: string;
    updatedAt: string;
    source: string;
  };
  variants: VariantRow[];
  incoming: IncomingLine[];
  totals: { stockValue: string; unknownCostQty: number; variants: number; onHand: number; onOrder: number };
}

/** What BrickLink says an item is, to fill in the new-item form. Not kept. */
export interface ItemLookupResponse {
  available: boolean;
  reason?: 'not_connected' | 'budget' | 'unreachable';
  message?: string;
  notOnBrickLink: boolean;
  item: {
    itemNo: string;
    name: string | null;
    categoryId: number | null;
    categoryName: string | null;
    yearReleased: number | null;
    weightG: number | null;
    dimX: number | null;
    dimY: number | null;
    dimZ: number | null;
    alternateNo: string | null;
    description: string | null;
    isObsolete: boolean | null;
  } | null;
  existingId: number | null;
}

// ---------------------------------------------------------------- purchase orders

export interface OrderLine {
  id: number;
  lineNo: number;
  variantId: number;
  itemId: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  imageUrl: string | null;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: Condition;
  subCondition: string | null;
  qtyOrdered: number;
  qtyReceived: number;
  outstanding: number;
  unitPrice: string;
  unitCost: string;
  unitCostBase: string | null;
  note: string | null;
  defaultLocationId: number | null;
}

export interface OrderReceipt {
  id: number;
  receivedOn: string;
  reference: string | null;
  notes: string | null;
  batchId: number;
  batchName: string;
  createdAt: string;
  /** Set when the receipt was reversed: its stock is gone and its pieces no longer count. */
  reversedAt: string | null;
  /** Every stock entry the receipt made is still untouched, so it can be reversed. */
  reversible: boolean;
  pieces: number;
  lines: { purchaseLineId: number; stockEntryId: number; qty: number; unitCost: string }[];
}

export interface OrderBlock {
  lines: OrderLine[];
  receipts: OrderReceipt[];
  charges: { items: string; shipping: string; insurance: string; other: string; tax: string; discount: string; total: string; recoverableTax: string; absorbed: string; postageTreatment: 'absorb' | 'spread' };
  currency: { code: string; defaultCode: string; rate: string | null; rateMissing: boolean };
  totals: { ordered: number; received: number; outstanding: number };
  locked: boolean;
  bricklinkArrived: boolean;
  /** Items on this order that are only basic records. */
  basicItemIds: number[];
  events: { id: number; at: string; kind: string; summary: string; sourceAt: string | null; by: string | null }[];
  inboundShipments: { id: number; reference: string; expectedReceiptOn: string; status: string; trackingNo: string | null }[];
}

export interface OrdersImportResponse {
  result: {
    from: string;
    listed: number;
    considered: number;
    created: number;
    updated: number;
    unchanged: number;
    cancelledSkipped: number;
    skipped: { orderId: string; reason: string }[];
    unreadable: number;
    itemsCreated: number;
    arrivedNotReceipted: number;
    stoppedBecause: string | null;
  };
}

// ---------------------------------------------------------------- bins from a CSV

export interface LocationImportRow {
  line: number;
  code: string;
  status: 'new' | 'update' | 'unchanged' | 'exists' | 'invalid';
  reason?: string;
  name: string | null;
  warehouse: string;
  walkOrder: number | null;
  parent: string | null;
}

export interface LocationImportResponse {
  result: {
    dryRun: boolean;
    rows: LocationImportRow[];
    skipped: { line: number; reason: string }[];
    warnings: string[];
    summary: { total: number; created: number; updated: number; unchanged: number; existing: number; invalid: number; skipped: number };
  };
}

// ---------------------------------------------------------------- the tabs of an item record

export interface InventoryLotRow {
  lotId: number;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: Condition;
  subCondition: string | null;
  status: string;
  qty: number;
  price: string | null;
  priceLocked: boolean;
  bricklinkLotId: number | null;
  avgCost: string | null;
  locations: { code: string | null; qty: number }[];
  listings: { channel: string; state: string; qty: number; price: string | null; status: string }[];
}
export interface ItemInventoryData {
  lots: InventoryLotRow[];
  totals: { qty: number; costValue: string; listValue: string };
}
export interface PurchaseTransactionRow {
  kind: 'received' | 'on_order';
  purchaseId: number;
  description: string;
  date: string;
  sellerName: string | null;
  status: OrderStatus;
  source: string;
  currency: string;
  colorName: string;
  condition: Condition;
  qty: number;
  qtyRemaining: number | null;
  unitCost: string;
  batch: string | null;
}
export interface ItemTransactionsData {
  purchases: PurchaseTransactionRow[];
  sales: { at: string; colorName: string; condition: Condition; qty: number; reason: string; refType: string | null; refId: number | null }[];
}
export interface ItemMovementsData {
  total: number;
  page: number;
  pageSize: number;
  rows: { id: number; at: string; colorName: string; condition: Condition; delta: number; reason: string; refType: string | null; refId: number | null; note: string | null; by: string | null; entryId: number }[];
}
// ---- Price history: the market's memory, drawn as a chart.

/** One sighting of BrickLink's price guide, as the chart takes it. */
export interface PriceHistoryPoint {
  at: string;
  currency: string | null;
  min: number | null;
  avg: number | null;
  qtyAvg: number | null;
  max: number | null;
  quantity: number | null;
  lots: number | null;
}

/** What a series says about the part's market price. */
export interface PriceTrend {
  state: 'rising' | 'falling' | 'flat' | 'too_little';
  latestAvg: number | null;
  earliestAvg: number | null;
  change: number | null;
  changePct: number | null;
  points: number;
  currency: string | null;
  from: string | null;
  to: string | null;
  summary: string;
}

/** One colour, condition and guide: a line on the chart. */
export interface PriceHistorySeries {
  colorId: number;
  colorName: string;
  /** The colour's own hex, when the catalogue knows it. */
  colorRgb: string | null;
  condition: Condition;
  guideType: 'sold' | 'stock';
  series: PriceHistoryPoint[];
  trend: PriceTrend;
}

export interface ItemPriceHistory {
  itemId: number;
  region: string;
  series: PriceHistorySeries[];
}

/** A part whose market price has moved enough to be worth a second look. */
export interface MovedPart {
  itemId: number;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  condition: Condition;
  guideType: 'sold' | 'stock';
  earliestAvg: number;
  latestAvg: number;
  change: number;
  changePct: number;
  currency: string | null;
  points: number;
  from: string;
  to: string;
}

export interface MovedPartsResponse {
  region: string;
  rows: MovedPart[];
}

/** A cached lowest listing that is meaningfully cheaper than the recent sold price. */
export interface DealRadarRow {
  itemId: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  condition: Condition;
  lowestListedPrice: string;
  usualSoldPrice: string;
  soldPriceBasis: 'quantity_weighted' | 'average';
  discountPct: number;
  currency: string;
  soldPieces: number;
  soldEvents: number | null;
  currentPieces: number | null;
  currentListings: number | null;
  stockFetchedAt: string | null;
  soldFetchedAt: string | null;
  buyList: { ruleId: number; available: number; onOrder: number; suggested: number } | null;
}

export interface DealRadarResponse {
  region: string;
  tracked: number;
  comparable: number;
  rows: DealRadarRow[];
}

export interface MarketFigures {
  currency: string | null;
  minPrice: string | null;
  avgPrice: string | null;
  maxPrice: string | null;
  lotCount: number | null;
  totalQuantity: number | null;
  status: 'ok' | 'unavailable';
  note: string | null;
  fetchedAt: string;
}
export interface MarketplaceRow {
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: Condition;
  bricklink: { sold: MarketFigures | null; forSale: MarketFigures | null };
  brickowl: { forSale: MarketFigures | null };
  ebay: { forSale: MarketFigures | null };
}
export interface MarketplaceStatus {
  key: 'bricklink' | 'brickowl' | 'ebay';
  name: string;
  state: 'off' | 'ready' | 'coming';
  note: string;
}
export interface ItemMarketplaces {
  region: string;
  marketplaces: MarketplaceStatus[];
  rows: MarketplaceRow[];
}
export interface LotListingRow {
  remoteId: string;
  qty: number;
  price: string | null;
  status: string;
  state: string;
  lastSeenAt: string;
}
export interface LotMarketplaceData {
  key: 'bricklink' | 'brickowl' | 'ebay';
  name: string;
  state: 'off' | 'ready' | 'coming';
  listings: LotListingRow[];
  market: { sold: MarketFigures | null; forSale: MarketFigures | null };
}
export interface LotMarketplacesData {
  lotId: number;
  itemId: number;
  price: string | null;
  currency: string;
  marketplaces: LotMarketplaceData[];
}
export interface BrickowlPriceRefreshResult {
  result: { looked: number; fresh: number; unmapped: { colorName: string; reason: string }[]; remaining: number; stoppedBecause: string | null };
}

export interface ItemMarketRow {
  colorName: string;
  condition: Condition;
  guideType: string;
  region: string;
  currency: string | null;
  minPrice: string | null;
  avgPrice: string | null;
  qtyAvgPrice: string | null;
  maxPrice: string | null;
  lotCount: number | null;
  totalQuantity: number | null;
  status: string;
  fetchedAt: string;
}
export interface SetUpItemsResponse {
  result: { processed: number; found: number; notOnBricklink: number; remaining: number; stoppedBecause: string | null };
}

export interface ProfitSummaryData {
  qty: number;
  unknownCostQty: number;
  unpricedQty: number;
  avgCost: string | null;
  avgPrice: string | null;
  profitEach: string | null;
  marginPct: number | null;
  profitTotal: string | null;
}
export interface ItemProfitabilityData {
  lots: {
    lotId: number;
    colorId: number;
    colorName: string;
    colorRgb: string | null;
    condition: Condition;
    qty: number;
    unknownCostQty: number;
    price: string | null;
    avgCost: string | null;
    profitEach: string | null;
    marginPct: number | null;
    profitTotal: string | null;
    layers: { entryId: number; purchaseId: number; purchase: string; date: string; batch: string; qty: number; unitCost: string; costUnknown: boolean }[];
  }[];
  colours: ({ colorId: number; colorName: string } & ProfitSummaryData)[];
  total: ProfitSummaryData;
}

// ---------------------------------------------------------------- suppliers

export interface SupplierStatsData {
  orders: number;
  cancelled: number;
  spent: string;
  piecesOrdered: number;
  piecesReceived: number;
  postagePct: number | null;
  avgDaysToReceive: number | null;
  firstOrder: string | null;
  lastOrder: string | null;
  stockPieces: number;
  stockCost: string;
  stockListValue: string;
}
export interface SupplierRowData extends SupplierStatsData {
  id: number;
  name: string;
  bricklinkUsername: string | null;
  contact: string | null;
  notes: string | null;
  tags: string[];
  active: boolean;
}

export interface CustomerStatsData {
  orders: number;
  cancelled: number;
  spent: string;
  firstOrder: string | null;
  lastOrder: string | null;
}
export interface CustomerRowData extends CustomerStatsData {
  id: number;
  name: string;
  bricklinkUsername: string | null;
  email: string | null;
  contact: string | null;
  notes: string | null;
  tags: string[];
  active: boolean;
}
export interface CustomerDetailData {
  customer: CustomerRowData;
  stats: CustomerStatsData;
  sales: SaleRowData[];
  addresses: CustomerAddressData[];
}
export interface CustomerAddressData { id: number; label: string; attention: string | null; addressee: string | null; phone: string | null; line1: string; line2: string | null; city: string; county: string | null; postcode: string; country: string; residential: boolean; defaultBilling: boolean; defaultShipping: boolean; }
export interface SupplierDetailData {
  supplier: { id: number; name: string; bricklinkUsername: string | null; contact: string | null; notes: string | null; tags: string[]; active: boolean; createdAt: string };
  stats: SupplierStatsData;
  orders: {
    id: number;
    purchasedOn: string;
    description: string;
    status: OrderStatus;
    source: string;
    externalId: string | null;
    totalCost: string;
    currency: string;
    shippingCost: string;
    piecesOrdered: number | null;
    piecesReceived: number | null;
  }[];
}

// ---------------------------------------------------------------- parting out sets

export interface PartOutPlanData {
  lotId: number;
  setNo: string;
  setName: string;
  condition: Condition;
  sets: number;
  setsOnHand: number;
  cost: string;
  pieces: number;
  rows: { itemType: ItemType; itemNo: string; name: string | null; colorId: number; colorName: string | null; colorRgb: string | null; qty: number; unitCost: string; knownPrice: string | null; itemExists: boolean }[];
  warnings: string[];
  missingColours: number[];
}
export interface PartOutResultData {
  purchaseId: number;
  receiptId: number | null;
  sets: number;
  expectedPieces: number;
  countedPieces: number;
  cost: string;
}

// ---------------------------------------------------------------- putting stock away

export interface BinSuggestionData {
  locationId: number;
  code: string;
  reason: 'holds' | 'usual';
  qtyThere: number;
}
export interface PutAwayRowData {
  entryId: number;
  lotId: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: Condition;
  qty: number;
  purchaseId: number;
  source: string;
  arrivedAt: string;
  suggestion: BinSuggestionData | null;
  alternatives: BinSuggestionData[];
}

export type ReportColumnKind = 'text' | 'int' | 'money' | 'unit' | 'pct' | 'date' | 'months';
export interface ReportData {
  key: string;
  title: string;
  about: string;
  columns: { key: string; label: string; kind: ReportColumnKind }[];
  rows: Record<string, string | number | null>[];
  total: number;
  options?: { name: string; label: string; value: string; choices: { value: string; label: string }[] }[];
  note?: string;
}
export interface ReportListItem {
  key: string;
  title: string;
  group: 'Stock' | 'Buying' | 'Pricing' | 'Sales' | 'Housekeeping';
  about: string;
}

export interface SaleRowData {
  id: number;
  externalId: string;
  orderedOn: string;
  buyerName: string | null;
  customerId: number | null;
  source: string;
  state: 'open' | 'shipped' | 'completed' | 'cancelled';
  sourceStatus: string;
  currency: string;
  itemsTotal: string;
  shipping: string;
  lines: number;
  pieces: number;
  cost: string;
  profit: string | null;
  stockApplied: boolean;
}
export interface SalesStatus {
  connected: boolean;
  salesFrom: string | null;
  adjustStock: boolean;
  lastPullAt: string | null;
  lastStatus: string | null;
}
export interface SalesImportResultData {
  from: string;
  listed: number;
  considered: number;
  created: number;
  updated: number;
  unchanged: number;
  cancelledSkipped: number;
  skipped: { orderId: string; reason: string }[];
  piecesNotInStock: number;
  stoppedBecause: string | null;
}
export interface PriceChangeRow {
  id: number;
  lotId: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorId: number;
  color: string;
  condition: string;
  oldPrice: string | null;
  newPrice: string;
  rule: string;
  reason: string | null;
  at: string;
  belowCost: boolean;
}

export interface SyncChange {
  field: string;
  from: string | null;
  to: string | null;
}
export interface SyncAction {
  lotId: number;
  kind: 'create' | 'update' | 'zero';
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  condition: string;
  remoteId: string | null;
  changes: SyncChange[];
}
export interface SyncPlan {
  actions: SyncAction[];
  offSale: SyncAction[];
  blocked: { lotId: number; itemNo: string; colorName: string; condition: string; reason: string; manualCompleteness?: { remoteId: string; subCondition: SubCondition } }[];
  summary: { create: number; update: number; zero: number; blocked: number; inSync: number; draftOnBrickLink: number; onlyOnBrickLink: number; wanted: number };
  listingsReadAt: string | null;
}
export interface SyncStatus {
  mode: 'off' | 'read_only' | 'read_write';
  connected: boolean;
  writesOn: boolean;
  lastReadAt: string | null;
  lastPushAt: string | null;
  lastPushStatus: string | null;
  lotsByStatus: Record<string, number>;
}
export interface SyncResult {
  attempted: number;
  succeeded: number;
  remaining: number;
  items: { lotId: number; itemNo: string; colorName: string; kind: string; ok: boolean; message: string | null }[];
  stoppedBecause: string | null;
  /** Fields BrickLink did not answer for, so they could not be checked. */
  unverified: string[];
}

export interface LotBricklinkInfo {
  lotId: number;
  bricklinkLotId: number | null;
  listing: { remoteId: string; qty: number; price: string | null; stockroom: boolean } | null;
  readAt: string | null;
  ambiguous: boolean;
  canTakeDown: boolean;
}

export interface BomLineData {
  id: number;
  componentItemId: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorId: number;
  colorName: string;
  colorRgb: string | null;
  condition: Condition | null;
  qty: number;
  source: string;
  onHandNew: number;
  onHandUsed: number;
}
export interface BomData {
  itemId: number;
  lines: BomLineData[];
  conditions: { condition: Condition; lotId: number | null; built: number; buildable: number; limitedBy: string | null }[];
}
